/**
 * Twilio Sink Admin Card — shown at the top of the Twilio incoming-sync
 * settings. A Twilio account/sink can be subscribed by multiple SaaS orgs,
 * but only ONE user-id is the "integration admin" for a given (orgId, appCode)
 * subscription. That user's personal preferences (assignment rules, ignored
 * channels, history-module mapping, etc.) are what the webhook fan-out CF
 * applies when delivering events into the org.
 *
 * The card lets the actual TPA owner see the current admin and switch to
 * any of the org users they've invited, via a "Change" button that opens
 * a dialog. Hidden for shared-access users — they're consuming the admin's
 * credentials, not configuring them.
 *
 * The current admin uid is reported to the parent via `onCurrentAdminChange`
 * so the parent can grey-out the rest of the incoming-sync settings when
 * a different user owns those preferences.
 *
 * Backend:
 *  - GET_TWILIO_SINK_MAP            → returns subscriptions[] (read-only fetch)
 *  - SET_TWILIO_SINK_MAP_SAAS_ADMIN → updates `userId` on the matching entry
 * See `app.azurna.com/functions/sms/sms-functions.js:259-316`.
 */
import { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Check, RefreshCw, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useSettingsStore } from '@/stores/settings-store';
import { callServiceAPI } from '@/lib/firebase/functions';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface SinkAdminCandidate {
  uid: string;
  name: string;
  email: string;
  picture?: string;
  isOwner: boolean;
}

interface SinkSubscription {
  orgId: string;
  appCode: string;
  userId: string;
}

export interface SinkAdminInfo {
  uid: string;
  name: string;
  email: string;
  isOwner: boolean;
}

/**
 * Module-level cache for GET_TWILIO_SINK_MAP. Dedupes the call across React
 * StrictMode's double-effect commits in dev and across fast navigate-away/
 * back remounts (a `useRef` is destroyed during those cycles, so we keep it
 * here instead).
 *
 * `subs` holds the last successful result so a remount within the window can
 * hydrate its fresh component state from the cache instead of either
 * refetching OR getting stuck on loading=true (the component instance is new
 * and its own fetch never ran).
 *
 * The 5s window is comfortably larger than the StrictMode gap and short
 * enough not to suppress real user-driven refetches.
 */
const _lastSinkMapFetch: {
  key: string | null;
  at: number;
  subs: SinkSubscription[] | null;
} = { key: null, at: 0, subs: null };

export function TwilioSinkAdminCard({
  extensionName,
  orgId,
  onCurrentAdminChange,
}: {
  extensionName: string;
  orgId: string;
  /**
   * Called whenever the current admin changes (after fetch + after switch).
   * Fires with `null` until both the uid and the resolved candidate are
   * available so the parent never has a uid without a corresponding name.
   * Re-fires when the candidate list later resolves a previously-unknown uid.
   */
  onCurrentAdminChange?: (admin: SinkAdminInfo | null) => void;
}) {
  const user = useAuthStore((s) => s.user);
  const isSharedUser = useAuthStore((s) => !!s.selectedAdminUserId);
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const orgUsersLoaded = useAppUsersStore((s) => s.loaded);
  const loadOrgUsers = useAppUsersStore((s) => s.loadOrgUsers);
  const fetchUserDetails = useAppUsersStore((s) => s.fetchUserDetails);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentAdminUid, setCurrentAdminUid] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(true);
  const [candidates, setCandidates] = useState<SinkAdminCandidate[]>([]);
  const [updatingUid, setUpdatingUid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // When the current admin's uid doesn't belong to this Ulgebra account's
  // manage-users list — e.g. another Ulgebra owner authorized the same
  // Twilio account with the same (orgId, appCode) pair — the candidates
  // list won't have them. Fetch their profile directly by uid so the card
  // shows the real owner instead of "Unknown user".
  const [externalAdmin, setExternalAdmin] = useState<SinkAdminCandidate | null>(null);

  // Trigger org users load (idempotent — store ignores if already loaded)
  useEffect(() => {
    if (!orgUsersLoaded) loadOrgUsers();
  }, [orgUsersLoaded, loadOrgUsers]);

  // Build candidate list: owner + invited users (resolve their firebase UIDs).
  // orgUsers entries store sanitized email-derived ids, not firebase UIDs;
  // fetchUserDetails returns the real UID (cached in IDB).
  useEffect(() => {
    let active = true;
    if (!user) return;
    (async () => {
      const cands: SinkAdminCandidate[] = [];
      cands.push({
        uid: user.uid,
        name: user.displayName || user.email,
        email: user.email,
        picture: user.photoURL || undefined,
        isOwner: true,
      });
      const seen = new Set<string>([user.uid, user.email.toLowerCase()]);
      for (const u of Object.values(orgUsers)) {
        if (!u.email || seen.has(u.email.toLowerCase())) continue;
        try {
          const details = await fetchUserDetails(u.email);
          if (!active) return;
          if (details?.uid && !seen.has(details.uid)) {
            cands.push({
              uid: details.uid,
              name: details.name || u.name || u.email,
              email: details.email || u.email,
              picture: details.photoURL || u.picture || undefined,
              isOwner: false,
            });
            seen.add(details.uid);
            seen.add((details.email || u.email).toLowerCase());
          }
        } catch {
          /* skip — user may not have signed in yet, no firebase doc */
        }
      }
      if (active) setCandidates(cands);
    })();
    return () => {
      active = false;
    };
  }, [user, orgUsers, fetchUserDetails]);

  // If the current admin's uid isn't in the manage-users candidates list,
  // resolve their profile by uid via GET_USERDETAILS_FOR_FBUID. Skipped when
  // the candidates list already covers it.
  useEffect(() => {
    if (!currentAdminUid) {
      setExternalAdmin(null);
      return;
    }
    if (candidates.find((c) => c.uid === currentAdminUid)) {
      setExternalAdmin(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const resp = await callServiceAPI({
          appCode: extensionName,
          TPAService: 'twilio',
          action: 'GET_USERDETAILS_FOR_FBUID',
          uaFBUserID: currentAdminUid,
          uaFBEmail: null,
          headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
        } as Record<string, unknown>);
        const data = resp.data as
          | { status?: string; userResponse?: { name?: string; email?: string; photoURL?: string; uid?: string } }
          | undefined;
        if (!active) return;
        if (data?.status === 'success' && data.userResponse) {
          setExternalAdmin({
            uid: currentAdminUid,
            name: data.userResponse.name || data.userResponse.email || '',
            email: data.userResponse.email || '',
            picture: data.userResponse.photoURL || undefined,
            isOwner: false,
          });
        }
      } catch (e) {
        console.error('[TwilioSinkAdmin] external admin fetch failed:', e);
      }
    })();
    return () => {
      active = false;
    };
  }, [currentAdminUid, candidates, extensionName]);

  // Notify parent whenever the current admin changes. Re-fires when the
  // candidates list later resolves the matching name/email so the overlay
  // can swap "another team member" for the real display name.
  useEffect(() => {
    if (!currentAdminUid) {
      onCurrentAdminChange?.(null);
      return;
    }
    const c = candidates.find((x) => x.uid === currentAdminUid) || externalAdmin;
    if (c) {
      onCurrentAdminChange?.({
        uid: c.uid,
        name: c.name,
        email: c.email,
        isOwner: c.isOwner,
      });
    } else {
      // uid known but candidate not yet resolved — emit minimal info so the
      // parent can still detect a mismatch and mount the overlay.
      onCurrentAdminChange?.({
        uid: currentAdminUid,
        name: '',
        email: '',
        isOwner: false,
      });
    }
  }, [currentAdminUid, candidates, externalAdmin, onCurrentAdminChange]);

  // Fetch sink map and resolve current admin for this (org, appCode) subscription.
  // Derive admin state from a subscriptions array. Used by both a fresh
  // fetch and the module-cache hydrate path so they can't diverge.
  const applySinkSubs = (subs: SinkSubscription[]) => {
    const match = subs.find((s) => s.orgId === orgId && s.appCode === extensionName);
    if (match) {
      setCurrentAdminUid(match.userId);
      setHasSubscription(true);
    } else {
      // No subscription yet for this org — sink map exists for the account
      // but this org hasn't been added. Default-display the owner so the
      // user understands what state they're in.
      setCurrentAdminUid(user?.uid ?? null);
      setHasSubscription(false);
    }
  };

  const fetchSinkMap = async (showSpinner: boolean) => {
    if (!user || !extensionName || !orgId) return;
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const resp = await callServiceAPI({
        headers: {},
        TPAService: 'twilio',
        action: 'GET_TWILIO_SINK_MAP',
        appCode: extensionName,
      } as Record<string, unknown>);
      const data = resp.data as { status?: string; subscriptions?: SinkSubscription[] } | undefined;
      if (data?.status === 'success') {
        const subs = data.subscriptions || [];
        // Cache the result so a remount within the dedup window can hydrate
        // from it instead of getting stuck on loading.
        _lastSinkMapFetch.subs = subs;
        _lastSinkMapFetch.at = Date.now();
        applySinkSubs(subs);
      } else {
        setError('Could not load sink configuration');
      }
    } catch (e) {
      console.error('[TwilioSinkAdmin] fetch failed:', e);
      setError('Failed to load sink configuration');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Wait until settings have finished loading before firing the first
  // fetch, otherwise we'd call GET_TWILIO_SINK_MAP twice on every page
  // load — once with hasSink=false (initial state) and again when
  // settings hydrate hasSink to its real value.
  //
  // After that initial fetch, only re-fire when the *boolean* sink-enabled
  // state flips (not on every sid write — discoverAndAdopt may write the
  // same SID back). Covers:
  //   • settings hydrated (single fetch on page load)
  //   • enable: null/'deleted' → real sid (false → true)
  //   • disable: real sid → 'deleted' (true → false)
  //
  // Dedupe via the module-level cache below so React StrictMode's
  // double-effect doesn't fire two CF calls per real transition in dev.
  const hasSink = useSettingsStore(
    (s) => !!s.uaTwilioSinkId && s.uaTwilioSinkId !== 'deleted'
  );
  const settingsLoaded = useSettingsStore((s) => s.settingsLoaded);
  useEffect(() => {
    if (!settingsLoaded || !user || !extensionName || !orgId) return;
    const key = `${extensionName}::${orgId}::${user.uid}::${hasSink}`;
    const now = Date.now();
    if (_lastSinkMapFetch.key === key && now - _lastSinkMapFetch.at < 5000) {
      // Recent fetch for the same state. On a remount (nav away + back,
      // StrictMode) this is a brand-new component instance with
      // loading=true and no data — hydrate from the cached result so it
      // doesn't sit stuck on the loading spinner. If the cache has no
      // result yet (first fetch still in flight), fall through to fetch
      // so loading still resolves.
      if (_lastSinkMapFetch.subs) {
        applySinkSubs(_lastSinkMapFetch.subs);
        setLoading(false);
        return;
      }
    }
    // New key (or stale window) — invalidate the cached result so an
    // in-flight remount can't hydrate from the previous key's data.
    _lastSinkMapFetch.key = key;
    _lastSinkMapFetch.at = now;
    _lastSinkMapFetch.subs = null;
    fetchSinkMap(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionName, orgId, user?.uid, hasSink, settingsLoaded]);

  const handleSwitch = async (newAdminUid: string) => {
    if (!extensionName || !orgId) return;
    if (newAdminUid === currentAdminUid) {
      setDialogOpen(false);
      return;
    }
    const prev = currentAdminUid;
    setUpdatingUid(newAdminUid);
    // Optimistic update so the parent can lift the overlay immediately
    setCurrentAdminUid(newAdminUid);
    try {
      const resp = await callServiceAPI({
        headers: {},
        TPAService: 'twilio',
        action: 'SET_TWILIO_SINK_MAP_SAAS_ADMIN',
        appCode: extensionName,
        orgId,
        adminId: newAdminUid,
      } as Record<string, unknown>);
      const data = resp.data as { status?: string } | undefined;
      if (data?.status === 'success') {
        toast.success('Integration admin updated');
        setDialogOpen(false);
      } else {
        setCurrentAdminUid(prev);
        toast.error('Could not update integration admin');
      }
    } catch (e) {
      console.error('[TwilioSinkAdmin] update failed:', e);
      setCurrentAdminUid(prev);
      toast.error('Could not update integration admin');
    } finally {
      setUpdatingUid(null);
    }
  };

  // Don't render for shared users — they don't own the integration
  if (isSharedUser || !user) return null;

  const currentAdmin =
    candidates.find((c) => c.uid === currentAdminUid) || externalAdmin || null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Integration Admin
              </CardTitle>
              <CardDescription>
                Whose preferences (assignment rules, ignored numbers, history module) apply to all
                incoming Twilio events for this organization.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchSinkMap(false)}
              disabled={loading || refreshing}
              className="shrink-0"
              title="Refresh"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading current admin…
            </div>
          ) : error ? (
            <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
              {error}
            </div>
          ) : (
            <>
              {!hasSubscription && (
                <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
                  This organization doesn&apos;t have a Twilio sink subscription yet. Enable the
                  webhook below to set one up — switching admin will be available afterwards.
                </div>
              )}
              <div
                className={cn(
                  'flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3',
                )}
              >
                <CandidateRow candidate={currentAdmin} />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDialogOpen(true)}
                  title={
                    !hasSubscription
                      ? 'No subscription to update yet'
                      : candidates.length <= 1
                        ? 'Invite team members to delegate the admin role'
                        : 'Change integration admin'
                  }
                  className="shrink-0"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  Change
                </Button>
              </div>
              {candidates.length === 1 && hasSubscription && (
                <p className="text-[11px] text-muted-foreground">
                  Invite team members from the Users settings to delegate the admin role.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <SwitchAdminDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        candidates={candidates}
        currentAdminUid={currentAdminUid}
        updatingUid={updatingUid}
        onSelect={handleSwitch}
      />
    </>
  );
}

/**
 * Compact row showing the current admin (or a placeholder if not yet resolved).
 */
function CandidateRow({
  candidate,
  fallbackEmail,
}: {
  candidate: SinkAdminCandidate | null;
  fallbackEmail?: string;
}) {
  if (!candidate) {
    return (
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-muted shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">
            Unknown user
          </p>
          {fallbackEmail && (
            <p className="text-xs text-muted-foreground truncate">{fallbackEmail}</p>
          )}
        </div>
      </div>
    );
  }
  const initial = (candidate.name || candidate.email).charAt(0).toUpperCase();
  return (
    <div className="flex items-center gap-3 min-w-0">
      {candidate.picture ? (
        <img
          src={candidate.picture}
          className="h-9 w-9 rounded-full object-cover shrink-0"
          alt=""
        />
      ) : (
        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium shrink-0">
          {initial}
        </div>
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium truncate">{candidate.name}</p>
          {candidate.isOwner && (
            <span className="shrink-0 rounded bg-muted px-1 py-0 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
              Owner
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{candidate.email}</p>
      </div>
    </div>
  );
}

function SwitchAdminDialog({
  open,
  onOpenChange,
  candidates,
  currentAdminUid,
  updatingUid,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: SinkAdminCandidate[];
  currentAdminUid: string | null;
  updatingUid: string | null;
  onSelect: (uid: string) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Switch Integration Admin</DialogTitle>
          <DialogDescription>
            Choose whose configuration should apply to all incoming Twilio events.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
          {candidates.map((c) => {
            const isActive = c.uid === currentAdminUid;
            const isUpdating = updatingUid === c.uid;
            const initial = (c.name || c.email).charAt(0).toUpperCase();
            return (
              <button
                key={c.uid}
                type="button"
                onClick={() => onSelect(c.uid)}
                disabled={!!updatingUid}
                className={cn(
                  'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                  isActive
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border bg-card hover:bg-accent/40',
                  !!updatingUid && !isUpdating && 'opacity-50 cursor-not-allowed',
                )}
              >
                {c.picture ? (
                  <img
                    src={c.picture}
                    className="h-9 w-9 rounded-full object-cover shrink-0"
                    alt=""
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-sm font-medium shrink-0">
                    {initial}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    {c.isOwner && (
                      <span className="shrink-0 rounded bg-muted px-1 py-0 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                        Owner
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                </div>
                <div className="shrink-0">
                  {isUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : isActive ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
